import { create } from 'zustand';
import { CAMERA_PERMISSION_STATE, CAMERA_STATUS, DEFAULT_CAPTURE_WIDTH } from './constants';
import { classifyCameraError } from './errors';
import { readCameraPreference, writeCameraPreference } from './persistence';
import { buildCameraList } from './device-list';
import { GENERIC_PERMISSION_VIDEO_CONSTRAINTS, openStreamWithFallback } from './constraints';
import { logCamera } from './debug';

const initialState = {
  permissionState: CAMERA_PERMISSION_STATE.UNKNOWN,
  cameraStatus: CAMERA_STATUS.IDLE,
  error: null,
  currentSettings: null,
  currentDeviceId: null,
  currentFacingMode: null,
  cameras: [],
};

function stopStreamTracks(stream) {
  stream?.getTracks?.().forEach(track => {
    try {
      track.stop();
    } catch (_error) {
      // Already stopped — nothing to do.
    }
  });
}

function readActiveSettings(stream) {
  return stream?.getVideoTracks?.()[0]?.getSettings?.() ?? null;
}

function isStreamLive(stream) {
  return Boolean(stream?.getVideoTracks?.().some(track => track.readyState === 'live'));
}

export const useCameraManagerStore = create((set, get) => {
  /**
   * Non-reactive companion state. The MediaStream itself must never live in
   * `set()` — it would trigger renders and get compared by identity (same
   * rationale services/realtime/store.js already applies to
   * RTCPeerConnection/AudioContext).
   */
  const runtime = {
    stream: null,
    // { name, promise } for whichever mutating action is currently in
    // flight, or null. Any call made while one is pending hands back that
    // SAME promise instead of racing a second getUserMedia.
    pendingOperation: null,
    // Bumped by stopCamera() (and by every locked op on entry). A mutating
    // op checks this again right before committing a newly-acquired stream —
    // if it no longer matches, a stopCamera() call landed while the op was
    // still in flight, and the stream it just opened is closed immediately
    // instead of silently reactivating a camera the caller already shut off.
    generation: 0,
  };

  const withCameraLock = (name, fn) => {
    if (runtime.pendingOperation) {
      logCamera('lock.busy', { requested: name, inFlight: runtime.pendingOperation.name });
      return runtime.pendingOperation.promise;
    }

    const myGeneration = ++runtime.generation;
    const promise = fn(myGeneration).finally(() => {
      if (runtime.pendingOperation?.promise === promise) {
        runtime.pendingOperation = null;
      }
    });
    runtime.pendingOperation = { name, promise };
    return promise;
  };

  // Commits a newly-acquired stream as the active one: stops whatever was
  // previously active (only now that the new one has actually succeeded),
  // persists the preference, updates reactive state. Callers must have
  // already confirmed `myGeneration` is still current before calling this.
  const applyNewStream = (stream, { previousStream, source } = {}) => {
    if (previousStream && previousStream !== stream) {
      stopStreamTracks(previousStream);
    }

    runtime.stream = stream;
    const settings = readActiveSettings(stream);

    writeCameraPreference({
      deviceId: settings?.deviceId ?? null,
      facingMode: settings?.facingMode ?? null,
      width: settings?.width ?? null,
      height: settings?.height ?? null,
    });

    set({
      permissionState: CAMERA_PERMISSION_STATE.GRANTED,
      cameraStatus: CAMERA_STATUS.READY,
      error: null,
      currentSettings: settings,
      currentDeviceId: settings?.deviceId ?? null,
      currentFacingMode: settings?.facingMode ?? null,
    });

    logCamera('stream.settings', { source, ...settings });
    return settings;
  };

  // The actual body of initializeCamera, factored out so
  // restoreCameraPreference can call it directly with the generation it
  // already holds — going through the exported, lock-wrapped
  // `initializeCamera` action from inside another locked action would just
  // hand back the outer action's own still-pending promise (the lock has no
  // way to tell "the same caller, one level down" apart from "a genuinely
  // concurrent second caller"), which deadlocks rather than running anything.
  const performInitializeCamera = async (myGeneration, options = {}) => {
    const targetWidth = options.width || DEFAULT_CAPTURE_WIDTH;
    const current = get().currentSettings;

    // Already holding a live stream that satisfies this request — reuse it
    // rather than tearing it down for an identical or lower-quality ask.
    // A caller after genuinely higher quality than what's live (e.g.
    // /exercise upgrading past camera-permission's small preview stream)
    // still falls through to a fresh acquisition below.
    const deviceMatches = !options.deviceId || current?.deviceId === options.deviceId;
    const widthSatisfies = (current?.width ?? 0) >= targetWidth;

    if (isStreamLive(runtime.stream) && deviceMatches && widthSatisfies) {
      logCamera('initialize.reuse-existing', current);
      return { ok: true, settings: current, error: null, reused: true };
    }

    set({ cameraStatus: CAMERA_STATUS.INITIALIZING });
    const previousStream = runtime.stream;

    try {
      const { stream, attempt } = await openStreamWithFallback({
        deviceId: options.deviceId,
        facingMode: options.facingMode,
        width: targetWidth,
      });

      if (myGeneration !== runtime.generation) {
        stopStreamTracks(stream);
        return { ok: false, settings: null, error: null, reused: false };
      }

      const settings = applyNewStream(stream, {
        previousStream,
        source: `initialize:${attempt}`,
      });
      logCamera('camera.selected', { source: 'initialize', attempt, ...settings });
      return { ok: true, settings, error: null, reused: false };
    } catch (rawError) {
      const classified = classifyCameraError(rawError);
      set({ cameraStatus: CAMERA_STATUS.ERROR, error: classified });
      logCamera('initialize.failed', classified);
      return { ok: false, settings: null, error: classified, reused: false };
    }
  };

  return {
    ...initialState,
    runtime,

    requestPermission: () =>
      withCameraLock('requestPermission', async myGeneration => {
        logCamera('permission.request', {});

        // Already holding a live stream (a second call from a remounted
        // page, or a call after initializeCamera already ran) — nothing to
        // (re)request.
        if (isStreamLive(runtime.stream)) {
          const settings = readActiveSettings(runtime.stream);
          set({ permissionState: CAMERA_PERMISSION_STATE.GRANTED });
          logCamera('permission.granted', { reused: true, ...settings });
          return { granted: true, stream: runtime.stream, settings, error: null };
        }

        try {
          const stream = await navigator.mediaDevices.getUserMedia(
            GENERIC_PERMISSION_VIDEO_CONSTRAINTS
          );

          if (myGeneration !== runtime.generation) {
            stopStreamTracks(stream);
            return { granted: false, stream: null, settings: null, error: null };
          }

          const settings = applyNewStream(stream, { source: 'permission' });
          logCamera('permission.granted', settings);
          return { granted: true, stream, settings, error: null };
        } catch (rawError) {
          const classified = classifyCameraError(rawError);
          // A true denial is the only outcome that should flip
          // permissionState — "no camera hardware"/"in use" are facts about
          // the device, not about whether the user refused access, and the
          // old requestMediaPermissions() collapsed all of them into `false`.
          set({
            permissionState:
              classified.code === 'permission-denied'
                ? CAMERA_PERMISSION_STATE.DENIED
                : CAMERA_PERMISSION_STATE.UNKNOWN,
            cameraStatus: CAMERA_STATUS.ERROR,
            error: classified,
          });
          logCamera('permission.failed', classified);
          return { granted: false, stream: null, settings: null, error: classified };
        }
      }),

    initializeCamera: (options = {}) =>
      withCameraLock('initializeCamera', myGeneration =>
        performInitializeCamera(myGeneration, options)
      ),

    enumerateCameras: async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = buildCameraList(devices, get().currentDeviceId);
        set({ cameras });
        logCamera('enumerate.result', {
          count: cameras.length,
          hasAnyNativeLabel: cameras.some(camera => camera.hasNativeLabel),
          currentDeviceId: get().currentDeviceId,
        });
        return { cameras, error: null };
      } catch (rawError) {
        const classified = classifyCameraError(rawError);
        logCamera('enumerate.failed', classified);
        return { cameras: [], error: classified };
      }
    },

    switchCamera: (target = {}) =>
      withCameraLock('switchCamera', async myGeneration => {
        const previousStream = runtime.stream;
        const previousSettings = get().currentSettings;
        set({ cameraStatus: CAMERA_STATUS.SWITCHING });

        try {
          const { stream, attempt } = await openStreamWithFallback({
            deviceId: target.deviceId,
            facingMode: target.facingMode,
            width: target.width || previousSettings?.width || DEFAULT_CAPTURE_WIDTH,
          });

          if (myGeneration !== runtime.generation) {
            stopStreamTracks(stream);
            return { ok: false, settings: null, error: null, keptExisting: false, attempt };
          }

          // Stop the old stream only now that the new one has actually
          // succeeded — a failed switch must never leave the patient with no
          // camera at all.
          const settings = applyNewStream(stream, { previousStream, source: `switch:${attempt}` });
          logCamera('camera.selected', { source: 'switch', attempt, ...settings });
          return { ok: true, settings, error: null, keptExisting: false, attempt };
        } catch (rawError) {
          const classified = classifyCameraError(rawError);
          // previousStream was never reassigned or stopped — it is still
          // `runtime.stream`, still live. This is the literal "keep the
          // existing working stream if switching fails" requirement.
          set({ cameraStatus: CAMERA_STATUS.READY, error: classified });
          logCamera('switch.failed', { target, name: classified.name, code: classified.code });
          return { ok: false, settings: null, error: classified, keptExisting: true };
        }
      }),

    stopCamera: (reason = 'manual') => {
      // Synchronous and independent of the lock — must always be able to
      // shut the camera off, even mid-operation. Bumping the generation is
      // what stops an in-flight initializeCamera/switchCamera from silently
      // reactivating the camera once its getUserMedia promise resolves.
      runtime.generation += 1;
      stopStreamTracks(runtime.stream);
      runtime.stream = null;

      set({
        cameraStatus: CAMERA_STATUS.STOPPED,
        currentSettings: null,
        currentDeviceId: null,
        currentFacingMode: null,
      });
      logCamera('stop', { reason });
    },

    restoreCameraPreference: (options = {}) =>
      withCameraLock('restoreCameraPreference', async myGeneration => {
        const stored = readCameraPreference();
        if (!stored) {
          // Nothing to restore is a distinct outcome from "restoration
          // failed" — callers that want a working camera regardless fall
          // back to a plain initializeCamera({}) themselves on this reason.
          return {
            restored: false,
            reason: 'no-stored-preference',
            preference: null,
            ok: false,
            settings: null,
            error: null,
          };
        }

        // `stored.width` is informational metadata about whatever resolution
        // was granted last time (which may well have been camera-permission's
        // deliberately small permission-probe stream) — never fed back as
        // the requested width here. A caller after real capture quality
        // (e.g. /exercise) passes its own `options.width`; otherwise this
        // falls through to openStreamWithFallback's own DEFAULT_CAPTURE_WIDTH.
        const targetWidth = options.width;

        let freshDevices;
        try {
          freshDevices = await navigator.mediaDevices.enumerateDevices();
        } catch (_error) {
          freshDevices = [];
        }

        if (myGeneration !== runtime.generation) {
          return {
            restored: false,
            reason: 'superseded',
            preference: stored,
            ok: false,
            settings: null,
            error: null,
          };
        }

        // "Still exists" is exact deviceId equality against a FRESH
        // enumeration, never the stale cached `cameras` list, and never a
        // label match — iOS labels are frequently blank/generic, and a false
        // label match risks silently opening a different physical camera
        // than the one the user actually chose.
        const stillExists = freshDevices.some(
          device => device.kind === 'videoinput' && device.deviceId === stored.deviceId
        );

        const result = await performInitializeCamera(
          myGeneration,
          stillExists
            ? { deviceId: stored.deviceId, width: targetWidth }
            : // Not found: ignore the stale id entirely and open the
              // default/generic camera instead — constrained by facingMode
              // only, never `{ deviceId: { exact: staleId } }`.
              { facingMode: stored.facingMode, width: targetWidth }
        );

        return {
          // "Restored" means the exact same device the patient chose came
          // back — a successful facingMode fallback onto a different camera
          // still leaves the caller a working stream (result.ok), just not
          // literally the same one, which is why `ok` is surfaced alongside
          // `restored` rather than folded into it.
          restored: Boolean(result.ok) && stillExists,
          reason: stillExists ? 'deviceId-matched' : 'device-not-found',
          preference: stored,
          ok: result.ok,
          settings: result.settings,
          error: result.error,
        };
      }),
  };
});
